import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2, MoreHorizontal, Pencil, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { formatDay, minorToText, toMinor } from "../lib/finance";
import {
  ASSET_TYPES, CONDITION_LABELS, COUNT_RESULT_LABELS, CURRENCIES, MOVE_ACTIONS, MOVE_LABELS, RETIRE_REASON_LABELS, availableMoves, canSeeRequests, money, partyPayload, partyProblem, partyText, procurementError, typeLabel,
} from "../lib/procurement";
import { formatDateTime } from "../lib/tickets";
import { MoveDialog } from "../components/procurement/AssetDialogs";
import {
  ConditionText, Field, FUNDER_KINDS, FUNDER_LABELS, LoanNote, PartyField, StatusBadge, WarrantyNote, usePeople,
} from "../components/procurement/ProcurementBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ResponsiveDropdownMenu, ResponsiveDropdownMenuContent, ResponsiveDropdownMenuItem, ResponsiveDropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";

const NONE = "__none__";

// One line of the journal, in words. The reason is only there for whoever manages assets (the server leaves it out otherwise).
function movementText(m) {
  switch (m.kind) {
    case "entry": return "Entrée dans l'inventaire";
    case "assign": return `Affecté à ${partyText(m.to_holder)}`;
    case "transfer": return `Transféré de ${partyText(m.from_holder)} à ${partyText(m.to_holder)}`;
    case "loan": return `Prêté à ${partyText(m.to_holder)}, retour prévu le ${formatDay(m.expected_return_on)}`;
    case "return": return `Restitué : ${partyText(m.to_holder)}`;
    case "location": return `Emplacement : ${m.to_location}`;
    case "owner_change": return `Propriétaire : ${partyText(m.from_owner)} → ${partyText(m.to_owner)}`;
    case "condition": return `État : ${CONDITION_LABELS[m.to_condition] || m.to_condition}`;
    case "retire": return `Retiré : ${RETIRE_REASON_LABELS[m.retire_reason] || m.retire_reason}`;
    case "count": return `Inventaire ${m.inventory || ""} : ${COUNT_RESULT_LABELS[m.count_result] || m.count_result}${m.observed_location ? ` (${m.observed_location})` : ""}`.replace("  ", " ");
    default: return MOVE_LABELS[m.kind] || m.kind;
  }
}

function EditDialog({ asset, people, suppliers, onClose, onDone }) {
  const hasCost = asset.cost_minor !== undefined;
  const hasFunder = asset.funder !== undefined;
  const [form, setForm] = useState({
    type: asset.type, label: "", brand: asset.brand || "", model: asset.model || "", serial_number: asset.serial_number || "", note: asset.note || "",
    cost: hasCost && asset.cost_minor != null ? minorToText(asset.cost_minor, asset.currency) : "", currency: asset.currency || "XOF",
    acquired_on: asset.acquired_on || "", supplier_id: asset.supplier?.id || "", warranty_until: asset.warranty_until || "", renewal_on: asset.renewal_on || "",
    funder: hasFunder ? { kind: asset.funder.kind, collaborator_id: asset.funder.collaborator_id || "", name: asset.funder.kind === "third_party" ? asset.funder.name || "" : "" } : null,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const blank = (v) => (v || "").trim() || null;
  const costProblem = hasCost && form.cost.trim() && toMinor(form.cost, form.currency) === null ? "Le coût n'est pas un montant valide." : "";
  const problem = costProblem || (form.funder ? partyProblem(form.funder, "Financeur") : "");
  const submit = async () => {
    setBusy(true);
    setError("");
    const body = { expected_version: asset.version, type: form.type, brand: blank(form.brand), model: blank(form.model), serial_number: blank(form.serial_number),
      note: blank(form.note), acquired_on: blank(form.acquired_on), supplier_id: blank(form.supplier_id), warranty_until: blank(form.warranty_until), renewal_on: blank(form.renewal_on) };
    if (form.label.trim()) body.label = form.label.trim();
    if (hasCost) {
      const cost = form.cost.trim() ? toMinor(form.cost, form.currency) : null;
      body.cost_minor = cost;
      body.currency = cost === null ? null : form.currency;
    }
    if (form.funder) body.funder = partyPayload(form.funder);
    try {
      const { data } = await api.patch(`/procurement/assets/${asset.id}`, body);
      toast.success("Actif modifié");
      onDone(data);
    } catch (e) {
      setError(procurementError(e, "Modification impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  // A plain function (not a component): a component defined here would be remounted at each keystroke and lose the focus.
  const txt = (id, label, type = "text", rest = {}) => (
    <div className="space-y-1.5"><Label htmlFor={`edit-${id}`}>{label}</Label>
      <Input id={`edit-${id}`} type={type} value={form[id]} onChange={(e) => set({ [id]: e.target.value })} className="h-10" data-testid={`asset-edit-${id}`} {...rest} /></div>
  );
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid="asset-edit-dialog">
        <DialogHeader><DialogTitle>Modifier l'actif</DialogTitle><DialogDescription>{asset.number}. L'emplacement, l'état, le détenteur et le propriétaire se changent par un mouvement, avec son historique.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(type) => set({ type })}>
              <SelectTrigger className="h-10" aria-label="Type d'actif"><SelectValue /></SelectTrigger>
              <SelectContent>{ASSET_TYPES.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">{txt("brand", "Marque", "text", { maxLength: 120 })}{txt("model", "Modèle", "text", { maxLength: 120 })}</div>
          {txt("label", "Libellé (laisser vide pour ne pas changer)", "text", { maxLength: 120 })}
          {txt("serial_number", "Numéro de série", "text", { maxLength: 120 })}
          {hasCost && (
            <div className="space-y-1.5">
              <Label htmlFor="edit-cost">Coût d'acquisition</Label>
              <div className="flex gap-2">
                <Input id="edit-cost" inputMode="decimal" value={form.cost} onChange={(e) => set({ cost: e.target.value })} className="h-10" data-testid="asset-edit-cost" />
                <Select value={form.currency} onValueChange={(currency) => set({ currency })}>
                  <SelectTrigger className="h-10 w-24" aria-label="Devise"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            {txt("acquired_on", "Acquis le", "date")}
            <div className="space-y-1.5">
              <Label>Fournisseur</Label>
              <Select value={form.supplier_id || NONE} onValueChange={(v) => set({ supplier_id: v === NONE ? "" : v })}>
                <SelectTrigger className="h-10" aria-label="Fournisseur"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>Aucun</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {txt("warranty_until", "Garantie jusqu'au", "date")}
            {txt("renewal_on", "Renouvellement le", "date")}
          </div>
          {form.funder && <PartyField id="edit-funder" label="Financeur" value={form.funder} onChange={(funder) => set({ funder })} kinds={FUNDER_KINDS} kindLabels={FUNDER_LABELS} people={people} />}
          <div className="space-y-1.5"><Label htmlFor="edit-note">Note interne</Label>
            <Textarea id="edit-note" rows={2} maxLength={1000} value={form.note} onChange={(e) => set({ note: e.target.value })} data-testid="asset-edit-note" /></div>
          {error && <p className="text-sm text-red-600" role="alert" data-testid="asset-edit-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy || !!problem} title={problem || undefined} data-testid="asset-edit-submit">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProcurementAsset() {
  const { assetId } = useParams();
  const { user } = useAuth();
  const { people } = usePeople();
  const [asset, setAsset] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [move, setMove] = useState(null);
  const [editing, setEditing] = useState(false);
  const [suppliers, setSuppliers] = useState([]);

  const load = useCallback(async () => {
    setPhase("loading");
    try {
      const { data } = await api.get(`/procurement/assets/${assetId}`);
      setAsset(data);
      setPhase("ready");
    } catch (e) {
      setError(e?.response?.status === 404 ? "Cet actif n'existe pas, ou vous n'y avez pas accès." : procurementError(e, "Impossible de charger l'actif."));
      setPhase("error");
    }
  }, [assetId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/procurement/suppliers", { params: { limit: 200 } }).then(({ data }) => setSuppliers(data.items)).catch(() => setSuppliers([])); }, []);

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-40 w-full" /></div>;
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3 max-w-xl mx-auto">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700" data-testid="asset-error">{error}</p>
        <div className="flex justify-center gap-2"><Button variant="outline" asChild><Link to="/achats/actifs">Retour aux actifs</Link></Button><Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button></div>
      </Card>
    );
  }

  const moves = asset.can_manage ? availableMoves(asset) : [];
  const [primary, ...others] = moves;
  const done = (data) => { setMove(null); setAsset(data); };

  return (
    <div className="space-y-4" data-testid="asset-page">
      <div>
        <Link to="/achats/actifs" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} />Actifs</Link>
        <div className="flex flex-wrap items-start justify-between gap-3 mt-1">
          <div className="min-w-0">
            <h1 className="font-display font-bold text-2xl text-slate-900 break-words">{asset.label}</h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
              <span className="text-sm text-slate-500 tabular-nums">{asset.number}</span><StatusBadge status={asset.status} /><LoanNote asset={asset} /><WarrantyNote asset={asset} />
            </div>
          </div>
          {asset.can_manage && asset.status !== "retired" && (
            <div className="flex items-center gap-2">
              {primary && <Button onClick={() => setMove(primary)} data-testid={`asset-action-${primary}`}>{MOVE_ACTIONS[primary]}</Button>}
              <Button variant="outline" onClick={() => setEditing(true)} data-testid="asset-edit"><Pencil size={14} className="mr-1.5" />Modifier</Button>
              {others.length > 0 && (
                <ResponsiveDropdownMenu>
                  <ResponsiveDropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" aria-label="Autres actions" data-testid="asset-more"><MoreHorizontal size={16} /></Button>
                  </ResponsiveDropdownMenuTrigger>
                  <ResponsiveDropdownMenuContent align="end" title="Autres actions">
                    {others.map((m) => (
                      <ResponsiveDropdownMenuItem key={m} onClick={() => setMove(m)} data-testid={`asset-action-${m}`} className={m === "retire" ? "text-red-600" : ""}>{MOVE_ACTIONS[m]}</ResponsiveDropdownMenuItem>
                    ))}
                  </ResponsiveDropdownMenuContent>
                </ResponsiveDropdownMenu>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={`grid gap-3 ${asset.funder !== undefined ? "md:grid-cols-3" : "md:grid-cols-2"}`} data-testid="asset-parties">
        {asset.funder !== undefined && (
          <Card className="p-4"><div className="text-xs uppercase tracking-wide text-slate-500">Qui a payé</div><div className="mt-1 font-medium text-slate-900" data-testid="asset-funder">{partyText(asset.funder)}</div>
            {asset.cost_minor != null && <div className="text-sm text-slate-500 tabular-nums">{money(asset.cost_minor, asset.currency)}</div>}</Card>
        )}
        <Card className="p-4"><div className="text-xs uppercase tracking-wide text-slate-500">À qui il appartient</div><div className="mt-1 font-medium text-slate-900" data-testid="asset-owner">{partyText(asset.owner)}</div>
          {asset.owner.kind === "to_confirm" && <div className="text-sm text-amber-700">Pas encore décidé : le payeur n'en est pas le propriétaire d'office.</div>}</Card>
        <Card className="p-4"><div className="text-xs uppercase tracking-wide text-slate-500">Qui l'a</div><div className="mt-1 font-medium text-slate-900" data-testid="asset-holder">{asset.status === "retired" ? "Retiré de l'inventaire" : partyText(asset.holder)}</div>
          {asset.status !== "retired" && asset.holder?.prospect_id && hasPermission(user, "prospects:view") && (
            <Link to={`/prospects/${asset.holder.prospect_id}`} className="mt-1 inline-block text-xs text-jamora underline" data-testid="asset-holder-pharmacy-link">Ouvrir la fiche de la pharmacie</Link>
          )}
          {asset.location && <div className="text-sm text-slate-500">{asset.location}</div>}</Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4 sm:p-5">
          <h2 className="font-semibold text-slate-900 mb-2">Informations</h2>
          <dl className="divide-y divide-slate-100">
            <Field label="Type">{typeLabel(asset.type)}</Field>
            {(asset.brand || asset.model) && <Field label="Marque et modèle">{[asset.brand, asset.model].filter(Boolean).join(" ")}</Field>}
            {asset.serial_number !== undefined && <Field label="Numéro de série"><span data-testid="asset-serial">{asset.serial_number || "—"}</span></Field>}
            <Field label="État"><ConditionText condition={asset.condition} /></Field>
            {asset.acquired_on !== undefined && <Field label="Acquis le">{asset.acquired_on ? formatDay(asset.acquired_on) : "—"}</Field>}
            {asset.supplier !== undefined && <Field label="Fournisseur">{asset.supplier ? asset.supplier.name : "—"}</Field>}
            {asset.origin && (
              <Field label="Achat d'origine">
                {canSeeRequests(user) ? <Link to={`/achats/demandes/${asset.origin.request_id}`} className="text-jamora underline" data-testid="asset-origin">{asset.origin.number}</Link>
                  : <span data-testid="asset-origin">{asset.origin.number}</span>}
              </Field>
            )}
            <Field label="Garantie">{asset.warranty_until ? formatDay(asset.warranty_until) : "—"}</Field>
            {asset.renewal_on && <Field label="Renouvellement">{formatDay(asset.renewal_on)}</Field>}
            {asset.note && <Field label="Note"><span className="whitespace-pre-wrap">{asset.note}</span></Field>}
            {asset.retired && <Field label="Retrait">{RETIRE_REASON_LABELS[asset.retired.reason]}</Field>}
          </dl>
        </Card>
        <Card className="p-4 sm:p-5" data-testid="asset-journal">
          <h2 className="font-semibold text-slate-900 mb-3">Journal des mouvements</h2>
          {!asset.movements ? <p className="text-sm text-slate-500">Le journal est réservé à la gestion de l'inventaire.</p> : (
            <ol className="space-y-3">
              {asset.movements.map((m) => (
                <li key={m.id} className="border-l-2 border-slate-200 pl-3" data-testid={`movement-${m.kind}`}>
                  <div className="text-sm text-slate-900">{movementText(m)}</div>
                  <div className="text-xs text-slate-500">{formatDateTime(m.at)}{m.actor_name ? ` · ${m.actor_name}` : ""}</div>
                  {m.reason && <div className="text-xs text-slate-600 mt-0.5">Motif : {m.reason}</div>}
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>

      {move && <MoveDialog asset={asset} kind={move} people={people} onClose={() => setMove(null)} onDone={done} />}
      {editing && <EditDialog asset={asset} people={people} suppliers={suppliers} onClose={() => setEditing(false)} onDone={(data) => { setEditing(false); setAsset(data); }} />}
    </div>
  );
}
